/**
 * BuoyLibrary.ts — manifest loader for navigation / mooring buoy variants.
 *
 * Each entry describes one renderable + simulated buoy kind:
 *   • visual: a single GLB file (own colours, topmarks, materials).
 *   • physics: ellipsoid radii + mass + default `anchored` flag, consumed
 *     by `Buoy` (sim/Buoy.ts) — same fields as before.
 *   • collision: simple sphere or cylinder centred on the buoy. The
 *     collision system uses an AABB derived from the world-space radii,
 *     so both shape options are flattened to the same axis-aligned box
 *     at runtime; the `type` is informational + future-proofing.
 *
 * Loaded once at app start from `public/buoys/index.json`.
 */

export type BuoyKind =
  | "mooring_standard"
  | "mooring_large"
  | "lateral_port"
  | "lateral_starboard"
  | "cardinal_n"
  | "cardinal_e"
  | "cardinal_s"
  | "cardinal_w";

export type BuoyCategory = "mooring" | "marker" | "lateral" | "cardinal";

export type BuoyCollisionShape = "sphere" | "cylinder" | "none";

export interface BuoyCollisionSpec {
  type: BuoyCollisionShape;
  /** When true (default) the buoy pushes the boat hull out. */
  solid?: boolean;
}

export interface BuoyEntry {
  id: BuoyKind;
  name: string;
  /** Path relative to public root, e.g. "buoys/mooring_standard.glb". */
  url: string;
  /** Horizontal ellipsoid radius (m). Drives buoyancy + collision width. */
  radiusXZ: number;
  /** Vertical ellipsoid half-height (m). Drives buoyancy + collision height. */
  radiusY: number;
  /** Mass (kg). Tuned so the buoy sits at the right waterline. */
  mass: number;
  /** When true (default) the buoy is moored to the seabed. */
  anchored?: boolean;
  collision: BuoyCollisionSpec;
  category: BuoyCategory;
  /** Cardinal direction tag (N/E/S/W) — only for cardinal marks. */
  cardinal?: "N" | "E" | "S" | "W";
}

export interface BuoysIndex {
  version: 1;
  buoys: BuoyEntry[];
}

export class BuoyLibrary {
  private readonly entries = new Map<BuoyKind, BuoyEntry>();

  private constructor(entries: BuoyEntry[]) {
    for (const e of entries) this.entries.set(e.id, e);
  }

  static async load(indexUrl = "buoys/index.json"): Promise<BuoyLibrary> {
    const idx = await fetchJson<BuoysIndex>(indexUrl);
    validateIndex(idx, indexUrl);
    return new BuoyLibrary(idx.buoys);
  }

  /** Build an empty library — used as a safe fallback when the manifest fails to load. */
  static empty(): BuoyLibrary {
    return new BuoyLibrary([]);
  }

  has(kind: BuoyKind): boolean {
    return this.entries.has(kind);
  }

  get(kind: BuoyKind): BuoyEntry {
    const e = this.entries.get(kind);
    if (!e) {
      throw new Error(
        `BuoyLibrary: unknown buoy kind "${kind}". Known: ${this.ids().join(", ") || "(none)"}`,
      );
    }
    return e;
  }

  tryGet(kind: BuoyKind): BuoyEntry | undefined {
    return this.entries.get(kind);
  }

  ids(): BuoyKind[] {
    return [...this.entries.keys()];
  }

  list(): BuoyEntry[] {
    return [...this.entries.values()];
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`fetch ${url} -> HTTP ${res.status}`);
  return (await res.json()) as T;
}

const KIND_VALUES: ReadonlySet<string> = new Set([
  "mooring_standard",
  "mooring_large",
  "lateral_port",
  "lateral_starboard",
  "cardinal_n",
  "cardinal_e",
  "cardinal_s",
  "cardinal_w",
]);

const CATEGORY_VALUES: ReadonlySet<string> = new Set([
  "mooring",
  "marker",
  "lateral",
  "cardinal",
]);

function validateIndex(
  idx: unknown,
  indexUrl: string,
): asserts idx is BuoysIndex {
  if (!idx || typeof idx !== "object") {
    throw new Error(`BuoyLibrary: malformed index at ${indexUrl}`);
  }
  const v = idx as Record<string, unknown>;
  if (v.version !== 1 || !Array.isArray(v.buoys)) {
    throw new Error(`BuoyLibrary: malformed index at ${indexUrl}`);
  }
  const seen = new Set<string>();
  for (const entry of v.buoys) validateEntry(entry, indexUrl, seen);
}

function validateEntry(
  entry: unknown,
  indexUrl: string,
  seen: Set<string>,
): asserts entry is BuoyEntry {
  if (!entry || typeof entry !== "object") {
    throw new Error(`BuoyLibrary: malformed entry in ${indexUrl}`);
  }
  const e = entry as Record<string, unknown>;
  const id = e.id;
  if (typeof id !== "string" || !KIND_VALUES.has(id)) {
    throw new Error(
      `BuoyLibrary: invalid buoy id "${String(id)}" in ${indexUrl}`,
    );
  }
  if (seen.has(id)) {
    throw new Error(`BuoyLibrary: duplicate buoy id "${id}" in ${indexUrl}`);
  }
  seen.add(id);
  if (typeof e.name !== "string" || e.name.length === 0) {
    throw new Error(`BuoyLibrary[${id}]: missing name`);
  }
  if (typeof e.url !== "string" || !/\.glb$/i.test(e.url)) {
    throw new Error(`BuoyLibrary[${id}]: url must point to a .glb`);
  }
  for (const f of ["radiusXZ", "radiusY", "mass"] as const) {
    const num = e[f];
    if (typeof num !== "number" || !Number.isFinite(num) || num <= 0) {
      throw new Error(`BuoyLibrary[${id}]: ${f} must be a positive number`);
    }
  }
  if (typeof e.category !== "string" || !CATEGORY_VALUES.has(e.category)) {
    throw new Error(`BuoyLibrary[${id}]: invalid category`);
  }
  const c = e.collision as Record<string, unknown> | undefined;
  if (!c || typeof c !== "object") {
    throw new Error(`BuoyLibrary[${id}]: missing collision spec`);
  }
  if (c.type !== "sphere" && c.type !== "cylinder" && c.type !== "none") {
    throw new Error(`BuoyLibrary[${id}]: invalid collision type`);
  }
}
